import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { ColumnMapping, SavedColumnMapping, SavedColumnMappingField } from '../../../models/bulk-payment.model';

@Component({
  selector: 'app-bulk-payment-column-mapping-step',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    MatCheckboxModule,
    MatIconModule,
    MatTableModule
  ],
  templateUrl: './column-mapping-step.component.html'
})
export class BulkPaymentColumnMappingStepComponent implements OnInit {
  @Input() rawData: string[][] = []; // Raw CSV data (array of string arrays)
  @Output() mappingComplete = new EventEmitter<ColumnMapping>();
  @Output() mappingChange = new EventEmitter<ColumnMapping | null>();

  columnMapping: ColumnMapping = {
    accountNumberColumn: -1,
    amountColumn: -1,
    invoiceNumberColumn: undefined,
    hasHeaders: true
  };

  availableColumns: { index: number; preview: string }[] = [];
  previewRows: string[][] = [];

  ngOnInit(): void {
    if (this.rawData.length > 0) {
      this.initializeColumns();
      this.preparePreview();
      this.autoDetectColumns();
    }
  }

  private initializeColumns(): void {
    const firstRow = this.rawData[0];
    this.availableColumns = firstRow.map((value, index) => ({
      index,
      preview: this.columnMapping.hasHeaders ? value : `Column ${index + 1}`
    }));
  }

  private preparePreview(): void {
    // Show first 5 rows for preview
    const startIndex = this.columnMapping.hasHeaders ? 1 : 0;
    this.previewRows = this.rawData.slice(startIndex, startIndex + 5);
  }

  private autoDetectColumns(): void {
    const firstRow = this.rawData[0];
    
    if (this.columnMapping.hasHeaders) {
      // Try to auto-detect based on header names
      firstRow.forEach((header, index) => {
        const normalized = header.toLowerCase().trim();
        
        if (normalized.includes('account') && normalized.includes('number')) {
          this.columnMapping.accountNumberColumn = index;
        } else if (normalized === 'amount' || normalized === 'amt') {
          this.columnMapping.amountColumn = index;
        } else if (normalized.includes('invoice')) {
          this.columnMapping.invoiceNumberColumn = index;
        }
      });
    }
    
    this.emitMappingChange();
  }

  onHasHeadersChange(): void {
    // Reset mappings when headers checkbox changes
    this.columnMapping.accountNumberColumn = -1;
    this.columnMapping.amountColumn = -1;
    this.columnMapping.invoiceNumberColumn = undefined;
    
    this.initializeColumns();
    this.preparePreview();
    this.autoDetectColumns();
  }

  onMappingChange(): void {
    this.emitMappingChange();
  }

  private emitMappingChange(): void {
    if (this.isValidMapping()) {
      this.mappingChange.emit(this.columnMapping);
    } else {
      this.mappingChange.emit(null);
    }
  }

  isValidMapping(): boolean {
    return this.columnMapping.accountNumberColumn >= 0 && 
           this.columnMapping.amountColumn >= 0;
  }

  getColumnPreview(columnIndex: number): string {
    if (columnIndex < 0 || columnIndex >= this.availableColumns.length) {
      return 'Not selected';
    }
    return this.availableColumns[columnIndex].preview;
  }

  getPreviewHeader(index: number): string {
    if (this.columnMapping.hasHeaders && this.rawData.length > 0) {
      return this.rawData[0][index] || `Column ${index + 1}`;
    }
    return `Column ${index + 1}`;
  }

  /**
   * Builds a SavedColumnMapping from the current mapping state, capturing
   * header text for each mapped column so it can be re-matched by name later.
   * Returns null when the mapping is not yet valid (account or amount unset).
   */
  getMappingForSave(): SavedColumnMapping | null {
    if (!this.isValidMapping()) {
      return null;
    }

    const headers = this.columnMapping.hasHeaders && this.rawData.length > 0
      ? this.rawData[0]
      : [];

    const toField = (index: number): SavedColumnMappingField => ({
      index,
      header: headers[index] ?? null,
    });

    const saved: SavedColumnMapping = {
      hasHeaders: this.columnMapping.hasHeaders,
      accountNumberColumn: toField(this.columnMapping.accountNumberColumn),
      amountColumn: toField(this.columnMapping.amountColumn),
    };

    if (
      this.columnMapping.invoiceNumberColumn !== undefined &&
      this.columnMapping.invoiceNumberColumn >= 0
    ) {
      saved.invoiceNumberColumn = toField(this.columnMapping.invoiceNumberColumn);
    }

    return saved;
  }

  /**
   * Applies a saved mapping to the current CSV.
   * For each field, first tries to match by header name (case-insensitive trim).
   * Falls back to the saved column index if within bounds; leaves unset otherwise.
   */
  applySavedMapping(saved: SavedColumnMapping): void {
    this.columnMapping.hasHeaders = saved.hasHeaders;
    this.initializeColumns();
    this.preparePreview();

    const currentHeaders = saved.hasHeaders && this.rawData.length > 0
      ? this.rawData[0].map((h) => h.toLowerCase().trim())
      : [];

    const resolveIndex = (field: SavedColumnMappingField): number => {
      if (saved.hasHeaders && field.header !== null) {
        const normalized = field.header.toLowerCase().trim();
        const found = currentHeaders.indexOf(normalized);
        if (found !== -1) {
          return found;
        }
      }
      return field.index < this.availableColumns.length ? field.index : -1;
    };

    this.columnMapping.accountNumberColumn = resolveIndex(saved.accountNumberColumn);
    this.columnMapping.amountColumn = resolveIndex(saved.amountColumn);

    if (saved.invoiceNumberColumn) {
      const idx = resolveIndex(saved.invoiceNumberColumn);
      this.columnMapping.invoiceNumberColumn = idx >= 0 ? idx : undefined;
    } else {
      this.columnMapping.invoiceNumberColumn = undefined;
    }

    this.emitMappingChange();
  }
}

